import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { X, BookOpen, Search, Sparkles, ChevronRight, HelpCircle } from "lucide-react";
import { GLOSSARY_TERMS, GlossaryTermData } from "../data/glossaryData";

// Global listeners registry to trigger side panel opening from any tooltip
let openGlossaryPanelCallback: ((termId: string) => void) | null = null;

export function registerGlossaryCallback(callback: (termId: string) => void) {
  openGlossaryPanelCallback = callback;
}

export function unregisterGlossaryCallback() {
  openGlossaryPanelCallback = null;
}

/**
 * Interactive Clickable Tooltip Component
 */
interface TooltipProps {
  key?: string;
  termId: string;
  text: string;
  language: "en" | "hr";
  onOpenGlossary?: (termId: string) => void;
}

export function InteractiveTooltip({ termId, text, language, onOpenGlossary }: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const term = GLOSSARY_TERMS.find(t => t.id === termId);
  const containerRef = useRef<HTMLSpanElement>(null);
  const [styleOffset, setStyleOffset] = useState<React.CSSProperties>({});
  const [arrowStyle, setArrowStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    setMounted(true);
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);
    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  // Calculate offsets when open on desktop
  useEffect(() => {
    if (!isOpen) {
      setStyleOffset({});
      setArrowStyle({});
      return;
    }

    const calculateOffsets = () => {
      if (!containerRef.current) return;

      const isDesktopCurrent = window.innerWidth >= 768;
      if (!isDesktopCurrent) {
        setStyleOffset({});
        setArrowStyle({});
        return;
      }

      const rect = containerRef.current.getBoundingClientRect();
      const wordCenter = rect.left + rect.width / 2;
      const viewportWidth = window.innerWidth;
      
      const tooltipWidth = 288; // matches w-72 (18rem)
      const halfWidth = tooltipWidth / 2;
      const leftEdge = wordCenter - halfWidth;
      const rightEdge = wordCenter + halfWidth;
      
      let shift = 0;
      const boundaryPadding = 16;

      if (leftEdge < boundaryPadding) {
        shift = boundaryPadding - leftEdge;
      } else if (rightEdge > viewportWidth - boundaryPadding) {
        shift = (viewportWidth - boundaryPadding) - rightEdge;
      }

      const maxArrowShift = halfWidth - 20;
      const arrowShift = Math.max(-maxArrowShift, Math.min(maxArrowShift, -shift));

      // Always show below on desktop for maximum visual safety and simple alignment
      setStyleOffset({
        position: "fixed",
        top: `${rect.bottom + 8}px`,
        left: `${rect.left + rect.width / 2 - 144 + shift}px`,
        width: "288px",
      });

      setArrowStyle({
        left: `calc(50% + ${arrowShift}px)`,
      });
    };

    let rAFId: number;
    const handleScrollOrResize = () => {
      cancelAnimationFrame(rAFId);
      rAFId = requestAnimationFrame(calculateOffsets);
    };

    const timer = setTimeout(handleScrollOrResize, 10);

    window.addEventListener("resize", handleScrollOrResize);
    window.addEventListener("scroll", handleScrollOrResize, true);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(rAFId);
      window.removeEventListener("resize", handleScrollOrResize);
      window.removeEventListener("scroll", handleScrollOrResize, true);
    };
  }, [isOpen]);

  // Click away listener for desktop popover
  useEffect(() => {
    if (!isOpen || !isDesktop) return;

    const handleOutsideClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleOutsideClick, true);
    return () => {
      document.removeEventListener("click", handleOutsideClick, true);
    };
  }, [isOpen, isDesktop]);

  if (!term) {
    return <span>{text}</span>;
  }

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  const handleGoToGlossary = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(false);
    if (onOpenGlossary) {
      onOpenGlossary(termId);
    } else if (openGlossaryPanelCallback) {
      openGlossaryPanelCallback(termId);
    }
  };

  const isPositioned = !isDesktop || (styleOffset.top !== undefined);

  const tooltipContent = (
    <div className="relative z-10 flex flex-col">
      {/* Holographic Subtle Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,212,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(0,212,255,0.015)_1px,transparent_1px)] bg-[size:10px_10px] pointer-events-none rounded-lg overflow-hidden"></div>

      <div className="relative z-10 flex items-center justify-between mb-3">
        <span className="font-display font-bold text-[13.5px] md:text-[13px] tracking-wider text-quantum-turquoise uppercase flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-quantum-turquoise animate-ping" />
          {language === "en" ? term.nameEn : term.nameHr}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(false);
          }}
          className="p-1 text-gray-400 hover:text-white rounded hover:bg-white/5 transition-all cursor-pointer"
          aria-label="Close Tooltip"
        >
          <X className="w-4 h-4 md:w-3 md:h-3" />
        </button>
      </div>

      <p className="relative z-10 text-gray-200 leading-relaxed mb-3 text-xs md:text-[13px]">
        {language === "en" ? term.definitionEn : term.definitionHr}
      </p>

      <button
        type="button"
        onClick={handleGoToGlossary}
        className="relative z-10 w-full flex items-center justify-between gap-2 px-3 py-1.5 bg-quantum-turquoise/10 hover:bg-quantum-turquoise/20 border border-quantum-turquoise/30 hover:border-quantum-turquoise/60 rounded text-quantum-turquoise font-mono text-[10px] tracking-wider uppercase transition-all cursor-pointer hover:scale-[1.02]"
        id={`tooltip-details-btn-${termId}`}
      >
        <span className="flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5" />
          {language === "en" ? "Deep-Dive Glossary" : "Dubinski Rječnik"}
        </span>
        <ChevronRight className="w-3 h-3" />
      </button>
    </div>
  );

  return (
    <span ref={containerRef} className="relative inline-block" id={`tooltip-wrapper-${termId}`}>
      {/* Interactive Word Trigger */}
      <button
        type="button"
        onClick={handleOpen}
        className="inline-flex items-center gap-0.5 border-b border-dotted border-quantum-turquoise/70 text-white font-medium hover:text-quantum-turquoise hover:border-quantum-turquoise hover:bg-quantum-turquoise/5 px-1 py-0.5 rounded transition-all cursor-pointer leading-relaxed text-left"
        title={language === "en" ? `Click to define "${text}"` : `Kliknite za definiciju "${text}"`}
        id={`term-btn-${termId}`}
      >
        <span>{text}</span>
        <Sparkles className="w-2.5 h-2.5 text-quantum-turquoise/60 inline-block animate-pulse shrink-0" />
      </button>

      {/* Popover Tooltip Dialog */}
      <AnimatePresence>
        {isOpen && mounted && isPositioned && createPortal(
          <>
            {/* Mobile-only background overlay */}
            {!isDesktop && (
              <div 
                className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-[2px] cursor-default"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
              />
            )}
            
            {/* Tooltip Card: Centered modal on mobile, fixed popover on desktop */}
            <motion.div
              initial={isDesktop ? { opacity: 0, scale: 0.95, y: 10 } : { opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={isDesktop ? { opacity: 0, scale: 0.95, y: 10 } : { opacity: 0, scale: 0.9, y: 15 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className={
                isDesktop
                  ? "fixed z-[1000] w-72 bg-cosmic-black/95 backdrop-blur-2xl border border-quantum-turquoise/50 rounded-lg p-5 shadow-[0_10px_30px_-10px_rgba(0,212,255,0.35)] font-sans text-left text-xs"
                  : "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1000] w-[85vw] sm:w-80 bg-cosmic-black/95 backdrop-blur-2xl border border-quantum-turquoise/50 rounded-lg p-5 shadow-[0_0_25px_rgba(0,212,255,0.3)] font-sans text-left text-xs"
              }
              style={isDesktop ? styleOffset : undefined}
              id={`tooltip-card-${termId}`}
            >
              {/* Arrow pointer: Only on desktop */}
              {isDesktop && (
                <>
                  <div 
                    className="absolute bottom-full -mb-[5px] border-x-[6px] border-x-transparent border-b-[6px] border-b-quantum-turquoise/45 -translate-x-1/2" 
                    style={arrowStyle}
                  />
                  <div 
                    className="absolute bottom-full -mb-[6px] border-x-[5px] border-x-transparent border-b-[5px] border-b-cosmic-black -translate-x-1/2" 
                    style={arrowStyle}
                  />
                </>
              )}

              {tooltipContent}
            </motion.div>
          </>,
          document.body
        )}
      </AnimatePresence>
    </span>
  );
}

/**
 * Main slide-out Cosmic Glossary side panel
 */
interface CosmicGlossaryProps {
  isOpen: boolean;
  onClose: () => void;
  language: "en" | "hr";
  activeTermId?: string | null;
  onSelectTerm?: (termId: string | null) => void;
}

export function CosmicGlossary({ isOpen, onClose, language, activeTermId, onSelectTerm }: CosmicGlossaryProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTermId, setExpandedTermId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Sync expanded term with selected active term ID from a tooltip click
  useEffect(() => {
    if (activeTermId) {
      setExpandedTermId(activeTermId);
      // Wait for animation to finish then scroll
      const timer = setTimeout(() => {
        const element = document.getElementById(`glossary-item-${activeTermId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          // Highlight flash effect
          element.classList.add("bg-quantum-turquoise/15");
          setTimeout(() => {
            element.classList.remove("bg-quantum-turquoise/15");
          }, 1500);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeTermId]);

  // Sync activeTermId back to parent if closed or reset
  const handleToggleTerm = (termId: string) => {
    const isExpanding = expandedTermId !== termId;
    setExpandedTermId(isExpanding ? termId : null);
    if (onSelectTerm) {
      onSelectTerm(isExpanding ? termId : null);
    }
  };

  // Filter glossary items based on search query
  const filteredTerms = GLOSSARY_TERMS.filter(term => {
    const name = language === "en" ? term.nameEn : term.nameHr;
    const def = language === "en" ? term.definitionEn : term.definitionHr;
    const details = language === "en" ? term.detailsEn : term.detailsHr;
    const query = searchQuery.toLowerCase();
    
    return (
      name.toLowerCase().includes(query) ||
      def.toLowerCase().includes(query) ||
      details.toLowerCase().includes(query)
    );
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop Blur Overlays */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm cursor-pointer"
            id="glossary-backdrop"
          />

          {/* Side Drawer Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md md:max-w-lg z-50 bg-cosmic-black/95 backdrop-blur-2xl border-l border-pleiadian-blue/20 flex flex-col shadow-[[-15px_0_35px_-5px_rgba(0,0,0,0.8)]]"
            id="glossary-side-panel"
          >
            {/* Sidebar Header */}
            <div className="p-6 border-b border-pleiadian-blue/20 flex items-center justify-between relative bg-cosmic-black/80">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-quantum-turquoise/10 rounded-lg border border-quantum-turquoise/20">
                  <BookOpen className="w-5 h-5 text-quantum-turquoise animate-pulse" />
                </div>
                <div>
                  <h3 className="text-base font-display font-bold uppercase tracking-widest text-white leading-none">
                    {language === "en" ? "Cosmic Glossary" : "Kozmički Rječnik"}
                  </h3>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-celestial-light mt-1.5 leading-none">
                    {language === "en" ? "Pleiadian Terms & Cosmology" : "Plejadanski pojmovi i kozmologija"}
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-all cursor-pointer flex items-center justify-center border border-transparent hover:border-pleiadian-blue/15"
                aria-label="Close Glossary"
                id="glossary-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search Input Bar */}
            <div className="p-4 border-b border-pleiadian-blue/10 bg-cosmic-black/40">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-celestial-light/50" />
                <input
                  type="text"
                  placeholder={language === "en" ? "Search cosmic database..." : "Pretraži kozmičku bazu podataka..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-cosmic-black/60 border border-pleiadian-blue/25 rounded-md pl-10 pr-4 py-2.5 text-xs text-white placeholder-celestial-light/40 focus:outline-none focus:border-quantum-turquoise focus:ring-1 focus:ring-quantum-turquoise transition-all font-mono"
                  id="glossary-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs cursor-pointer"
                    aria-label={language === "en" ? "Clear Search" : "Očisti pretragu"}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* List Body of Terms */}
            <div 
              ref={listRef}
              className="flex-1 overflow-y-auto px-6 py-4 space-y-4 scrollbar-thin scrollbar-track-cosmic-black scrollbar-thumb-pleiadian-blue/20"
              id="glossary-terms-list"
            >
              {filteredTerms.length > 0 ? (
                filteredTerms.map((term) => {
                  const isExpanded = expandedTermId === term.id;
                  const name = language === "en" ? term.nameEn : term.nameHr;
                  const definition = language === "en" ? term.definitionEn : term.definitionHr;
                  const details = language === "en" ? term.detailsEn : term.detailsHr;

                  return (
                    <div
                      key={term.id}
                      id={`glossary-item-${term.id}`}
                      className={`border rounded-lg transition-all duration-300 ${
                        isExpanded
                          ? "bg-gradient-to-b from-cosmic-indigo/25 to-cosmic-black border-quantum-turquoise/50 shadow-[0_4px_20px_rgba(0,212,255,0.1)]"
                          : "bg-cosmic-black/40 border-pleiadian-blue/15 hover:border-pleiadian-blue/30"
                      }`}
                    >
                      {/* Term Row Header button */}
                      <button
                        onClick={() => handleToggleTerm(term.id)}
                        className="w-full text-left p-4 flex items-start justify-between gap-4 cursor-pointer"
                        id={`glossary-header-${term.id}`}
                        aria-expanded={isExpanded}
                      >
                        <div className="space-y-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-mono font-semibold tracking-wider uppercase bg-gradient-to-r ${term.archetypeColor} text-white shadow-sm`}>
                            {term.id.replace("_", " ")}
                          </span>
                          <h4 className="text-sm font-display font-bold text-white tracking-wide mt-1.5 flex items-center gap-1.5">
                            {name}
                          </h4>
                          {!isExpanded && (
                            <p className="text-xs text-gray-400 font-sans line-clamp-1 mt-1 leading-relaxed">
                              {definition}
                            </p>
                          )}
                        </div>
                        <div className="pt-2 shrink-0">
                          <motion.div
                            animate={{ rotate: isExpanded ? 90 : 0 }}
                            transition={{ duration: 0.2 }}
                            className="p-1 rounded bg-pleiadian-blue/10 border border-pleiadian-blue/15 text-quantum-turquoise"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </motion.div>
                        </div>
                      </button>

                      {/* Expended deep dive text details */}
                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.25, ease: "easeInOut" }}
                            className="overflow-hidden border-t border-pleiadian-blue/10"
                          >
                            <div className="p-4 bg-cosmic-black/60 text-xs leading-relaxed space-y-3 font-sans">
                              {/* Short Definition block */}
                              <div className="bg-quantum-turquoise/5 border-l-2 border-quantum-turquoise p-2.5 rounded-r">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-quantum-turquoise block mb-0.5">
                                  {language === "en" ? "Primary Definition" : "Primarna Definicija"}
                                </span>
                                <p className="text-gray-200">{definition}</p>
                              </div>

                              {/* Detailed analysis paragraph */}
                              <div className="text-gray-300 space-y-2">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-celestial-light block">
                                  {language === "en" ? "Detailed Cosmology" : "Detaljna Kozmologija"}
                                </span>
                                <p className="text-gray-300 text-[11.5px] leading-relaxed">
                                  {details}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })
              ) : (
                <div className="py-12 text-center text-gray-500 font-mono text-xs space-y-2" id="glossary-no-results">
                  <HelpCircle className="w-8 h-8 mx-auto text-celestial-light/20 animate-bounce" />
                  <p>{language === "en" ? "No galactic matches found" : "Nema podudaranja u kozmičkoj bazi"}</p>
                </div>
              )}
            </div>

            {/* Footer indicator status */}
            <div className="p-4 border-t border-pleiadian-blue/20 text-center font-mono text-[9px] text-celestial-light/40 uppercase tracking-widest bg-cosmic-black/80">
              {language === "en" ? "HOLOGRAPHIC GLOSSARY SYSTEM M45" : "HOLOGRAFSKI RJEČNIK SUSTAVA M45"}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Text Enricher Utility function
 * Processes strings, detects keywords, and wraps them in InteractiveTooltip
 * (Simplified to return plain text as requested by the user)
 */
export function parseTextWithGlossary(
  text: string,
  language: "en" | "hr",
  onTermClick?: (id: string) => void
): React.ReactNode {
  return <>{text}</>;
}
